import React, { useEffect, useRef, useState } from 'react';
import { useCentralChat, litoWhatsAppUrl } from '../services/centralChat';
import './LitoChat.css';

function WhatsAppIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
        </svg>
    );
}

/* LitoChat: floating "Consultar a Lito" panel. Hosts central-chat when configured, otherwise offers WhatsApp */
function LitoChat({ message, onClose, panelOpen }) {
    const [minimized, setMinimized] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const panelRef = useRef(null);
    const hostRef = useRef(null);
    const open = Boolean(message);
    const chatStatus = useCentralChat({ active: open, message, hostRef });

    // Each new consultation expands the panel and takes focus
    useEffect(() => {
        if (!message) return;
        setMinimized(false);
        if (panelRef.current) panelRef.current.focus();
    }, [message]);

    useEffect(() => {
        if (!open || minimized) return;
        // Esc first shrinks an expanded chat, then closes it
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (expanded && (chatStatus === 'loading' || chatStatus === 'ready')) setExpanded(false);
            else onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, minimized, expanded, chatStatus, onClose]);

    useEffect(() => {
        if (!open) setExpanded(false);
    }, [open]);

    if (!open) return null;

    const showCentralChat = chatStatus === 'loading' || chatStatus === 'ready';
    const isExpanded = expanded && showCentralChat; // the WhatsApp fallback always uses the normal size
    const waUrl = litoWhatsAppUrl(message);

    return (
        <>
            {isExpanded && !minimized && (
                <div className="lito-backdrop" onClick={() => setExpanded(false)} aria-hidden="true" />
            )}
            <section
                ref={panelRef}
                className={`lito-chat${showCentralChat ? ' lito-chat--embed' : ''}${isExpanded ? ' is-expanded' : ''}${minimized ? ' is-minimized' : ''}`}
                role="dialog"
                aria-labelledby="lito-chat-title"
                tabIndex={-1}
            >
                <header className="lito-chat__header">
                    <div className="lito-avatar" aria-hidden="true">L</div>
                    <div className="lito-chat__who">
                        <h2 id="lito-chat-title">Lito</h2>
                        <p><span className="lito-dot" aria-hidden="true" />Atención al contribuyente</p>
                    </div>
                    <button type="button" className="lito-icon-btn" onClick={() => { setMinimized(true); setExpanded(false); }} aria-label="Minimizar chat" title="Minimizar">
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                    {showCentralChat && (
                        <button
                            type="button"
                            className="lito-icon-btn"
                            onClick={() => setExpanded((v) => !v)}
                            aria-label={expanded ? 'Achicar chat' : 'Agrandar chat'}
                            aria-pressed={expanded}
                            title={expanded ? 'Achicar' : 'Agrandar'}
                        >
                            {expanded ? (
                                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            )}
                        </button>
                    )}
                    <button type="button" className="lito-icon-btn" onClick={onClose} aria-label="Cerrar chat" title="Cerrar">
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                </header>

                <div className={`lito-chat__body${showCentralChat ? ' lito-chat__body--embed' : ''}`}>
                    {showCentralChat ? (
                        <>
                            {chatStatus === 'loading' && (
                                <div className="lito-chat__loading" role="status">
                                    <span className="lito-spinner" aria-hidden="true" />
                                    Conectando con Lito…
                                </div>
                            )}
                            <div ref={hostRef} className="lito-chat__host" />
                        </>
                    ) : (
                        <>
                            <div className="lito-msg lito-msg--in">
                                ¡Hola! Soy Lito. Te ayudo con boletas, planes de pago, libre deuda, cambio de responsable de pago y otros trámites.
                            </div>
                            <div className="lito-msg lito-msg--out">{message}</div>
                            <p className="lito-chat__hint">Tu consulta se enviará por WhatsApp. Podés editarla antes de enviarla.</p>
                        </>
                    )}
                </div>

                {!showCentralChat && (
                    <footer className="lito-chat__footer">
                        <a className="lito-cta" href={waUrl} target="_blank" rel="noopener noreferrer">
                            <WhatsAppIcon />
                            Continuar por WhatsApp
                        </a>
                    </footer>
                )}
            </section>

            {minimized && (
                <button type="button" className={`lito-launcher${panelOpen ? ' panel-open' : ''}`} onClick={() => setMinimized(false)} aria-label="Abrir chat con Lito">
                    <span className="lito-avatar lito-avatar--sm" aria-hidden="true">L</span>
                    <span>Consulta a Lito</span>
                </button>
            )}
        </>
    );
}

export default LitoChat;
